import { useMemo } from 'react';

import { isPartialPolicyPayload, TypedPolicy } from '../../../types';

import {
  DEFAULT_ACCESS_MODE,
  KUBERNETES_SETUP_FORM_INITIAL_VALUES,
  KUBERNETES_SETUP_INITIAL_VALUES,
  KubernetesSetupFormValues,
  KubernetesSetupPolicyPayload,
} from './types';

/**
 * Type guard to check if data is KubernetesSetupPolicyPayload
 */
function isKubernetesSetupPolicyPayload(
  data: unknown
): data is KubernetesSetupPolicyPayload {
  return typeof data === 'object' && data !== null && 'UseLoadBalancer' in data;
}

/**
 * Hook to get initial values for Kubernetes Setup Policy form.
 *
 * Returns complete form values including common fields (name, environmentGroupIds)
 * and type-specific fields.
 *
 * @param policy - Optional policy to load values from (for edit mode)
 * @param templateData - Optional template data to pre-fill form (for create from template)
 * @returns Complete form initial values
 */
export function useKubernetesSetupInitialValues(
  policy?: TypedPolicy<unknown>,
  templateData?: Record<string, unknown>
): KubernetesSetupFormValues {
  return useMemo(() => {
    const defaults = KUBERNETES_SETUP_FORM_INITIAL_VALUES;

    // Template mode: apply template data over defaults
    if (
      !policy &&
      templateData &&
      isPartialPolicyPayload<KubernetesSetupPolicyPayload>(templateData)
    ) {
      return {
        name: '',
        environmentGroupIds: [],
        ...deserializePayload(templateData, defaults),
      };
    }
    // Create mode: use defaults
    if (!policy) {
      return defaults;
    }

    // Edit mode: deserialize from policy data
    const data = policy.Data;
    if (!isKubernetesSetupPolicyPayload(data)) {
      return {
        // Common fields from policy metadata
        name: policy.Name,
        environmentGroupIds: policy.EnvironmentGroups ?? [],
        // Type-specific defaults (using type-specific only, not full form values)
        ...KUBERNETES_SETUP_INITIAL_VALUES,
      };
    }

    return {
      name: policy.Name,
      environmentGroupIds: policy.EnvironmentGroups ?? [],
      ...deserializePayload(data, defaults),
    };
  }, [policy, templateData]);
}

function deserializePayload(
  data: Partial<KubernetesSetupPolicyPayload>,
  defaults: KubernetesSetupFormValues
) {
  return {
    // Type-specific fields from policy data
    useLoadBalancer: data.UseLoadBalancer?.value ?? defaults.useLoadBalancer,
    // Map from PascalCase API payload to camelCase form data
    ingressControllers: Array.isArray(data.IngressClasses)
      ? data.IngressClasses.map((ic) => ({ name: ic.value?.Name ?? '' }))
      : defaults.ingressControllers,
    allowNoneIngressClass:
      data.AllowNoneIngressClass?.value ?? defaults.allowNoneIngressClass,
    ingressAvailabilityPerNamespace:
      data.IngressAvailabilityPerNamespace?.value ??
      defaults.ingressAvailabilityPerNamespace,
    restrictStandardUserIngressW:
      data.RestrictStandardUserIngressW?.value ??
      defaults.restrictStandardUserIngressW,
    changeWindow: data.EndpointChangeWindow?.value ?? defaults.changeWindow,
    enableResourceOverCommit:
      data.EnableResourceOverCommit?.value ?? defaults.enableResourceOverCommit,
    resourceOverCommitPercentage:
      data.ResourceOverCommitPercentage?.value ??
      defaults.resourceOverCommitPercentage,
    useServerMetrics: data.UseServerMetrics?.value ?? defaults.useServerMetrics,
    // Map from PascalCase API payload to camelCase form data
    storageClasses: Array.isArray(data.StorageClasses)
      ? data.StorageClasses.map((sc) => ({
          name: sc.value?.Name ?? '',
          accessModes: sc.value?.AccessModes ?? [DEFAULT_ACCESS_MODE],
          allowVolumeExpansion: sc.value?.AllowVolumeExpansion ?? false,
        }))
      : defaults.storageClasses,
  };
}
